import {
  arePortTypesCompatible,
  type GraphNode,
  type GraphEdge,
  type NodeDefBase,
} from './nodeDefs.js';
import { RENDER_NODE_DEFS } from './renderNodeDefs.js';
import { createGraphDebugAliasScope, formatGraphIssueMessage } from './debugLabels.js';

export interface GeometryEvalResult {
  distance: number;
  coverage?: number;
  layoutBounds: [number, number, number, number];
  contourBounds: [number, number, number, number];
  transform: number[];
}

export function topoSort(nodes: GraphNode[], edges: GraphEdge[]): string[] {
  const adj: Record<string, string[]> = {};
  const inDeg: Record<string, number> = {};
  nodes.forEach(n => { adj[n.id] = []; inDeg[n.id] = 0; });
  edges.forEach(e => {
    if (adj[e.fromNode]) adj[e.fromNode].push(e.toNode);
    if (inDeg[e.toNode] !== undefined) inDeg[e.toNode]++;
  });
  const queue = nodes.filter(n => inDeg[n.id] === 0).map(n => n.id);
  const sorted: string[] = [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    sorted.push(id);
    (adj[id] || []).forEach(nb => {
      inDeg[nb]--;
      if (inDeg[nb] === 0) queue.push(nb);
    });
  }
  return sorted;
}

export interface GraphCompilePlan {
  nodes: GraphNode[];
  sortedIds: string[];
  edgeMap: Record<string, GraphEdge>;
  nodeMap: Record<string, GraphNode>;
  outputNodeId: string | null;
}

export function buildGraphCompilePlan(nodes: GraphNode[], edges: GraphEdge[]): GraphCompilePlan {
  const sortedIds = topoSort(nodes, edges);
  const edgeMap: Record<string, GraphEdge> = {};
  edges.forEach(edge => {
    edgeMap[`${edge.toNode}:${edge.toPort}`] = edge;
  });
  const nodeMap: Record<string, GraphNode> = {};
  nodes.forEach(node => {
    nodeMap[node.id] = node;
  });
  const outputNode = nodes.find(node => node.type === 'Output') ?? null;
  return {
    nodes,
    sortedIds,
    edgeMap,
    nodeMap,
    outputNodeId: outputNode?.id ?? null
  };
}

export type DrawBounds = [number, number, number, number];

export interface GraphIssue {
  level: 'error' | 'warning';
  code: 'missing_output' | 'cycle' | 'missing_output_input' | 'dangling_edge' | 'type_mismatch' | 'node_not_allowed' | 'material_output_not_color' | 'data_invalid';
  message: string;
  nodeIds: string[];
}

export interface GraphValidationResult {
  issues: GraphIssue[];
  nodeIssues: Record<string, GraphIssue[]>;
}

export function validateGraph(
  nodes: GraphNode[],
  edges: GraphEdge[],
  graphSlot: 'geometry' | 'material' = 'geometry',
  nodeDefs?: Record<string, NodeDefBase>,
): GraphValidationResult {
  const issues: GraphIssue[] = [];
  const nodeMap: Record<string, GraphNode> = {};
  nodes.forEach(node => {
    nodeMap[node.id] = node;
  });

  const pushIssue = (issue: GraphIssue): void => {
    issues.push(issue);
  };

  const defs: Record<string, NodeDefBase> = nodeDefs ?? RENDER_NODE_DEFS;
  const outputNodeType = 'Output';

  for (const node of nodes) {
    const def = defs[node.type] as (NodeDefBase & { graphs?: ('geometry' | 'material')[] }) | undefined;
    const allowed = def?.graphs ?? ['geometry', 'material'];
    if (!allowed.includes(graphSlot)) {
      pushIssue({
        level: 'error',
        code: 'node_not_allowed',
        message: `${node.type} is not available in ${graphSlot === 'geometry' ? 'Shape Graph' : 'Material Graph'}.`,
        nodeIds: [node.id],
      });
    }
  }

  const outputNodes = nodes.filter(node => node.type === outputNodeType);
  if (outputNodes.length === 0) {
    pushIssue({
      level: 'error',
      code: 'missing_output',
      message: 'Add an Output node to render the graph.',
      nodeIds: []
    });
  }

  const sorted = topoSort(nodes, edges);
  const aliases = createGraphDebugAliasScope(sorted, nodes);
  if (sorted.length !== nodes.length) {
    const sortedSet = new Set(sorted);
    const cycleNodes = nodes.filter(node => !sortedSet.has(node.id)).map(node => node.id);
    pushIssue({
      level: 'error',
      code: 'cycle',
      message: 'Cycle detected in the graph.',
      nodeIds: cycleNodes
    });
  }

  const occupiedInputs = new Set(edges.map(edge => `${edge.toNode}:${edge.toPort}`));
  for (const edge of edges) {
    const fromNode = nodeMap[edge.fromNode];
    const toNode = nodeMap[edge.toNode];
    if (!fromNode || !toNode) {
      pushIssue({
        level: 'error',
        code: 'dangling_edge',
        message: 'Edge points to a missing node.',
        nodeIds: [edge.fromNode, edge.toNode].filter(Boolean)
      });
      continue;
    }

    const fromDef = defs[fromNode.type] as NodeDefBase | undefined;
    const toDef = defs[toNode.type] as NodeDefBase | undefined;
    if (!fromDef || !toDef) continue;
    const outputs = typeof fromDef.outputs === 'function' ? fromDef.outputs(fromNode) : fromDef.outputs;
    const inputs = typeof toDef.inputs === 'function' ? toDef.inputs(toNode) : toDef.inputs;
    const outType = outputs[edge.fromPort]?.type;
    const inType = inputs[edge.toPort]?.type;

    if (!outType || !inType) {
      pushIssue({
        level: 'error',
        code: 'dangling_edge',
        message: 'Edge points to a missing port.',
        nodeIds: [edge.fromNode, edge.toNode]
      });
      continue;
    }

    if (!arePortTypesCompatible(outType, inType, toNode.type)) {
      pushIssue({
        level: 'error',
        code: 'type_mismatch',
        message: `Type mismatch: ${outType} -> ${inType}.`,
        nodeIds: [edge.fromNode, edge.toNode]
      });
    }
  }

  for (const outputNode of outputNodes) {
    if (!occupiedInputs.has(`${outputNode.id}:0`)) {
      pushIssue({
        level: 'warning',
        code: 'missing_output_input',
        message: 'Output node has no input connected.',
        nodeIds: [outputNode.id]
      });
    }
  }

  if (graphSlot === 'material') {
    for (const outputNode of outputNodes) {
      const edge = edges.find(candidate => candidate.toNode === outputNode.id && candidate.toPort === 0);
      if (!edge) continue;
      const fromNode = nodeMap[edge.fromNode];
      const fromDef = fromNode ? defs[fromNode.type] as NodeDefBase | undefined : null;
      const outputs = fromNode && fromDef
        ? (typeof fromDef.outputs === 'function' ? fromDef.outputs(fromNode) : fromDef.outputs)
        : [];
      const outType = outputs[edge.fromPort]?.type;
      if (outType !== 'vec3' && outType !== 'vec4') {
        pushIssue({
          level: 'error',
          code: 'material_output_not_color',
          message: 'Material Output must be color. Add a Colorize node before Output.',
          nodeIds: [edge.fromNode, outputNode.id]
        });
      }
    }
  }

  const formattedIssues = issues.map(issue => ({
    ...issue,
    message: formatGraphIssueMessage(issue.message, issue.nodeIds, nodeMap, aliases),
  }));

  const nodeIssues: Record<string, GraphIssue[]> = {};
  for (const issue of formattedIssues) {
    for (const nodeId of issue.nodeIds) {
      if (!nodeIssues[nodeId]) nodeIssues[nodeId] = [];
      nodeIssues[nodeId].push(issue);
    }
  }

  return { issues: formattedIssues, nodeIssues };
}
